<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useNotificationsStore } from '@/stores/notifications'
import { usePipelineApprovalApi } from '@/composables/usePipelineApprovalApi'
import { toast } from 'vue-sonner'
import { formatDistanceToNow } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import type { Notification, NotificationEntityType } from '@/types'
import type { NotificationGroupData } from '@/stores/notifications/getters'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Bell,
  CheckCheck,
  FolderKanban,
  Layers,
  FileText,
  Ticket,
  FileCheck,
  ShoppingCart,
  MessageSquare,
  Calendar,
  Package,
  Trash2,
  Video,
  IterationCcw,
  X,
  ChevronDown,
  Users,
  UserPlus,
} from 'lucide-vue-next'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const { t, locale } = useI18n()
const router = useRouter()
const notificationsStore = useNotificationsStore()
const { approvePipeline } = usePipelineApprovalApi()

const activeTab = ref('all')

// --- Aprobación de pipeline (notificaciones accionables) ---
// La notificación trae data: { action: 'approve_pipeline', pipelineId, phase }.
const isApprovalNotification = (n: Notification): boolean =>
  n.data?.action === 'approve_pipeline' && typeof n.data?.pipelineId === 'string'

// id de la notificación cuya acción está en vuelo (deshabilita sus botones)
const submittingId = ref<string | null>(null)

// Estado del diálogo de rechazo
const rejectDialogOpen = ref(false)
const rejectReason = ref('')
const rejectTarget = ref<Notification | null>(null)

const handleApprove = async (e: Event, notification: Notification) => {
  e.stopPropagation()
  const pipelineId = notification.data?.pipelineId
  if (typeof pipelineId !== 'string' || submittingId.value) return
  submittingId.value = notification.id
  try {
    const { message } = await approvePipeline(pipelineId, true)
    if (!notification.isRead) await notificationsStore.markAsRead(notification.id)
    toast('Pipeline aprobado', { description: message || undefined })
  } catch (err) {
    toast('No se pudo aprobar', {
      description: err instanceof Error ? err.message : 'Error inesperado',
    })
  } finally {
    submittingId.value = null
  }
}

const openRejectDialog = (e: Event, notification: Notification) => {
  e.stopPropagation()
  rejectTarget.value = notification
  rejectReason.value = ''
  rejectDialogOpen.value = true
}

const confirmReject = async () => {
  const notification = rejectTarget.value
  const pipelineId = notification?.data?.pipelineId
  const reason = rejectReason.value.trim()
  if (!notification || typeof pipelineId !== 'string' || !reason) return
  submittingId.value = notification.id
  try {
    const { message } = await approvePipeline(pipelineId, false, reason)
    if (!notification.isRead) await notificationsStore.markAsRead(notification.id)
    toast('Pipeline rechazado', { description: message || undefined })
    rejectDialogOpen.value = false
    rejectTarget.value = null
    rejectReason.value = ''
  } catch (err) {
    toast('No se pudo rechazar', {
      description: err instanceof Error ? err.message : 'Error inesperado',
    })
  } finally {
    submittingId.value = null
  }
}

const dateLocale = computed(() => locale.value === 'es' ? es : enUS)

const allNotifications = computed(() => notificationsStore.notifications)
const unreadNotifications = computed(() => notificationsStore.unreadNotifications)
const groupedNotifications = computed(() => notificationsStore.groupedNotifications)
const collapsedGroups = ref<Set<string>>(new Set())

const formatDate = (date: Date) => {
  return formatDistanceToNow(date, { addSuffix: true, locale: dateLocale.value })
}

const getNotificationIcon = (type: string) => {
  if (type.includes('project')) return FolderKanban
  if (type.includes('epic')) return Layers
  if (type.includes('requirement')) return FileText
  if (type.includes('ticket')) return Ticket
  if (type.includes('quotation')) return FileCheck
  if (type.includes('sales_order')) return ShoppingCart
  if (type.includes('petition')) return MessageSquare
  if (type.includes('appointment')) return Calendar
  if (type.includes('release')) return Package
  if (type.includes('comment')) return MessageSquare
  if (type.includes('meeting')) return Video
  if (type.includes('sprint')) return IterationCcw
  if (type.includes('prospect')) return Users
  if (type.includes('lead')) return UserPlus
  return Bell
}

const getGroupIcon = (group: string) => {
  const icons: Record<string, typeof Bell> = {
    projects: FolderKanban,
    backlog: FileText,
    meetings: Video,
    support: Ticket,
    commercial: ShoppingCart,
    releases: Package,
    crm: Users,
  }
  return icons[group] || Bell
}

const toggleGroup = (group: string) => {
  if (collapsedGroups.value.has(group)) {
    collapsedGroups.value.delete(group)
  } else {
    collapsedGroups.value.add(group)
  }
}

const getEntityRoute = (entityType?: NotificationEntityType, entityId?: string): string | null => {
  if (!entityType || !entityId) return null

  const routes: Record<NotificationEntityType, string> = {
    project: `/projects/${entityId}`,
    epic: `/epics/${entityId}`,
    requirement: `/requirements/${entityId}`,
    ticket: `/tickets/${entityId}`,
    quotation: `/quotations/${entityId}`,
    salesOrder: `/sales-orders/${entityId}`,
    petition: `/petitions/${entityId}`,
    appointment: `/calendar`,
    release: `/releases/${entityId}`,
    application: `/projects/${entityId}`,
    meeting: `/projects/${entityId}`,
    sprint: `/backlog`,
    prospect: `/crm`,
    lead: `/leads/${entityId}`,
    deliverable: `/deliverables/${entityId}`,
  }

  return routes[entityType] || null
}

const handleNotificationClick = async (notification: Notification) => {
  // Las notificaciones accionables (aprobar pipeline) se resuelven con sus botones,
  // no navegando al tocar la fila.
  if (isApprovalNotification(notification)) return

  if (!notification.isRead) {
    await notificationsStore.markAsRead(notification.id)
  }

  const route = getEntityRoute(notification.entityType, notification.entityId)
  if (route) {
    emit('update:open', false)
    router.push(route)
  }
}

const handleDelete = async (e: Event, notificationId: string) => {
  e.stopPropagation()
  await notificationsStore.deleteNotification(notificationId)
}

const handleMarkAllAsRead = async () => {
  await notificationsStore.markAllAsRead()
}

const handleDeleteAllRead = async () => {
  await notificationsStore.deleteAllRead()
}

const goToNotifications = () => {
  emit('update:open', false)
  router.push('/notifications')
}
</script>

<template>
  <Sheet :open="props.open" @update:open="emit('update:open', $event)">
    <SheetContent side="right" class="w-full sm:max-w-xl lg:max-w-2xl p-0 flex flex-col">
      <SheetHeader class="px-6 py-4 border-b">
        <div class="flex items-center justify-between">
          <SheetTitle class="flex items-center gap-2">
            <Bell class="h-5 w-5" />
            Notificaciones
            <span v-if="notificationsStore.unreadCount > 0" class="text-sm text-muted-foreground font-normal">
              ({{ notificationsStore.unreadCount }} sin leer)
            </span>
          </SheetTitle>
          <div class="flex items-center gap-1">
            <Button
              v-if="notificationsStore.unreadCount > 0"
              variant="ghost"
              size="sm"
              class="h-8"
              @click="handleMarkAllAsRead"
            >
              <CheckCheck class="h-4 w-4 mr-1" />
              Marcar todas
            </Button>
            <Button
              v-if="notificationsStore.readNotifications.length > 0"
              variant="ghost"
              size="sm"
              class="h-8 text-muted-foreground"
              @click="handleDeleteAllRead"
            >
              <Trash2 class="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetHeader>

      <Tabs v-model="activeTab" class="flex-1 flex flex-col min-h-0">
        <div class="px-4 pt-2">
          <TabsList class="w-full">
            <TabsTrigger value="all" class="flex-1">
              Todas
            </TabsTrigger>
            <TabsTrigger value="unread" class="flex-1">
              Sin leer
              <span
                v-if="notificationsStore.unreadCount > 0"
                class="ml-1.5 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-xs"
              >
                {{ notificationsStore.unreadCount }}
              </span>
            </TabsTrigger>
            <TabsTrigger value="grouped" class="flex-1">
              Agrupadas
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="all" class="flex-1 mt-0 min-h-0">
          <ScrollArea class="h-full">
            <div v-if="allNotifications.length === 0" class="p-6 text-center text-muted-foreground">
              <Bell class="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No hay notificaciones</p>
            </div>

            <div v-else class="divide-y">
              <div
                v-for="notification in allNotifications"
                :key="notification.id"
                class="p-4 hover:bg-accent/50 cursor-pointer transition-colors group"
                :class="{ 'bg-accent/30': !notification.isRead }"
                @click="handleNotificationClick(notification)"
              >
                <div class="flex gap-3">
                  <div class="shrink-0 mt-0.5">
                    <div
                      class="h-8 w-8 rounded-full flex items-center justify-center"
                      :class="notification.isRead ? 'bg-muted' : 'bg-primary/10'"
                    >
                      <component
                        :is="getNotificationIcon(notification.type)"
                        class="h-4 w-4"
                        :class="notification.isRead ? 'text-muted-foreground' : 'text-primary'"
                      />
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between gap-2">
                      <p
                        class="text-sm"
                        :class="notification.isRead ? 'text-muted-foreground' : 'font-medium'"
                      >
                        {{ notification.title }}
                      </p>
                      <div class="flex items-center gap-1 shrink-0">
                        <div v-if="!notification.isRead" class="h-2 w-2 rounded-full bg-primary" />
                        <button
                          class="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10"
                          @click="(e: Event) => handleDelete(e, notification.id)"
                        >
                          <X class="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    </div>
                    <p class="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                      {{ notification.message }}
                    </p>
                    <div v-if="isApprovalNotification(notification)" class="mt-2 space-y-2">
                      <Badge variant="secondary" class="text-[10px]">Aprobación requerida</Badge>
                      <div class="flex items-center gap-2">
                        <Button
                          size="sm"
                          class="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          :disabled="submittingId === notification.id"
                          @click="(e: Event) => handleApprove(e, notification)"
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          class="h-7 border-destructive/50 text-destructive hover:bg-destructive/10"
                          :disabled="submittingId === notification.id"
                          @click="(e: Event) => openRejectDialog(e, notification)"
                        >
                          Rechazar
                        </Button>
                      </div>
                    </div>
                    <p class="text-xs text-muted-foreground mt-1">
                      {{ formatDate(notification.createdAt) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="unread" class="flex-1 mt-0 min-h-0">
          <ScrollArea class="h-full">
            <div v-if="unreadNotifications.length === 0" class="p-6 text-center text-muted-foreground">
              <CheckCheck class="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Todo al dia</p>
            </div>

            <div v-else class="divide-y">
              <div
                v-for="notification in unreadNotifications"
                :key="notification.id"
                class="p-4 hover:bg-accent/50 cursor-pointer transition-colors bg-accent/30 group"
                @click="handleNotificationClick(notification)"
              >
                <div class="flex gap-3">
                  <div class="shrink-0 mt-0.5">
                    <div class="h-8 w-8 rounded-full flex items-center justify-center bg-primary/10">
                      <component
                        :is="getNotificationIcon(notification.type)"
                        class="h-4 w-4 text-primary"
                      />
                    </div>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between gap-2">
                      <p class="text-sm font-medium">
                        {{ notification.title }}
                      </p>
                      <div class="flex items-center gap-1 shrink-0">
                        <div class="h-2 w-2 rounded-full bg-primary" />
                        <button
                          class="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10"
                          @click="(e: Event) => handleDelete(e, notification.id)"
                        >
                          <X class="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    </div>
                    <p class="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                      {{ notification.message }}
                    </p>
                    <div v-if="isApprovalNotification(notification)" class="mt-2 space-y-2">
                      <Badge variant="secondary" class="text-[10px]">Aprobación requerida</Badge>
                      <div class="flex items-center gap-2">
                        <Button
                          size="sm"
                          class="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          :disabled="submittingId === notification.id"
                          @click="(e: Event) => handleApprove(e, notification)"
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          class="h-7 border-destructive/50 text-destructive hover:bg-destructive/10"
                          :disabled="submittingId === notification.id"
                          @click="(e: Event) => openRejectDialog(e, notification)"
                        >
                          Rechazar
                        </Button>
                      </div>
                    </div>
                    <p class="text-xs text-muted-foreground mt-1">
                      {{ formatDate(notification.createdAt) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="grouped" class="flex-1 mt-0 min-h-0">
          <ScrollArea class="h-full">
            <div v-if="groupedNotifications.length === 0" class="p-6 text-center text-muted-foreground">
              <Bell class="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No hay notificaciones</p>
            </div>

            <div v-else class="py-1">
              <Collapsible
                v-for="group in groupedNotifications"
                :key="group.group"
                :default-open="true"
                class="border-b last:border-b-0"
              >
                <CollapsibleTrigger
                  class="flex w-full items-center justify-between px-4 py-2.5 hover:bg-accent/50 transition-colors"
                  @click="toggleGroup(group.group)"
                >
                  <div class="flex items-center gap-2">
                    <component :is="getGroupIcon(group.group)" class="h-4 w-4 text-muted-foreground" />
                    <span class="text-sm font-medium">{{ group.label }}</span>
                    <span v-if="group.unreadCount > 0" class="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-xs">
                      {{ group.unreadCount }}
                    </span>
                    <span class="text-xs text-muted-foreground">({{ group.notifications.length }})</span>
                  </div>
                  <ChevronDown
                    class="h-4 w-4 text-muted-foreground transition-transform"
                    :class="{ '-rotate-90': collapsedGroups.has(group.group) }"
                  />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div class="divide-y">
                    <div
                      v-for="notification in group.notifications"
                      :key="notification.id"
                      class="px-4 py-3 hover:bg-accent/50 cursor-pointer transition-colors group"
                      :class="{ 'bg-accent/30': !notification.isRead }"
                      @click="handleNotificationClick(notification)"
                    >
                      <div class="flex gap-3 pl-6">
                        <div class="flex-1 min-w-0">
                          <div class="flex items-start justify-between gap-2">
                            <p
                              class="text-sm"
                              :class="notification.isRead ? 'text-muted-foreground' : 'font-medium'"
                            >
                              {{ notification.title }}
                            </p>
                            <div class="flex items-center gap-1 shrink-0">
                              <div v-if="!notification.isRead" class="h-2 w-2 rounded-full bg-primary" />
                              <button
                                class="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10"
                                @click="(e: Event) => handleDelete(e, notification.id)"
                              >
                                <X class="h-3 w-3 text-muted-foreground hover:text-destructive" />
                              </button>
                            </div>
                          </div>
                          <p class="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                            {{ notification.message }}
                          </p>
                          <div v-if="isApprovalNotification(notification)" class="mt-2 space-y-2">
                            <Badge variant="secondary" class="text-[10px]">Aprobación requerida</Badge>
                            <div class="flex items-center gap-2">
                              <Button
                                size="sm"
                                class="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                                :disabled="submittingId === notification.id"
                                @click="(e: Event) => handleApprove(e, notification)"
                              >
                                Aprobar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                class="h-7 border-destructive/50 text-destructive hover:bg-destructive/10"
                                :disabled="submittingId === notification.id"
                                @click="(e: Event) => openRejectDialog(e, notification)"
                              >
                                Rechazar
                              </Button>
                            </div>
                          </div>
                          <p class="text-xs text-muted-foreground mt-0.5">
                            {{ formatDate(notification.createdAt) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      <Separator />
      <div class="p-4">
        <Button
          variant="outline"
          class="w-full"
          @click="goToNotifications"
        >
          Ver todas las notificaciones
        </Button>
      </div>
    </SheetContent>

    <Dialog v-model:open="rejectDialogOpen">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rechazar pipeline</DialogTitle>
          <DialogDescription>
            Indica el motivo del rechazo. Es obligatorio.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          v-model="rejectReason"
          placeholder="Motivo del rechazo…"
          rows="3"
          :disabled="submittingId !== null"
        />
        <DialogFooter>
          <Button
            variant="outline"
            :disabled="submittingId !== null"
            @click="rejectDialogOpen = false"
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            :disabled="!rejectReason.trim() || submittingId !== null"
            @click="confirmReject"
          >
            Rechazar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Sheet>
</template>
